// ============================================================================
// BANK ACCOUNT SERVICE
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { bankAccountEndpoints } from '../endpoints/bankAccount.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  BankAccount,
  BankAccountForBooking,
  CreateBankAccountRequest,
  UpdateBankAccountRequest,
  BankAccountSearchRequest,
} from '../types';

class BankAccountService {
  async getAll(params?: BankAccountSearchRequest): Promise<PaginatedResponse<BankAccount>> {
    const endpoint = params
      ? buildEndpointWithQuery('/BankAccount', params)
      : bankAccountEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw.data ?? {};

    return {
      success: raw.success,
      message: raw.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<BankAccount>> {
    const response = await get<ApiResponse<BankAccount>>(bankAccountEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<BankAccount>> {
    const response = await get<ApiResponse<BankAccount>>(bankAccountEndpoints.getByCode(code));
    return response.data;
  }

  /**
   * Returns bank accounts available to a property's bookings — property-mapped first
   * (with primary first within), then shared/fallback accounts. Used by the
   * PaymentCaptureModal dropdown.
   */
  async getForBooking(propertyId: ID): Promise<ApiResponse<BankAccountForBooking[]>> {
    const endpoint = `${bankAccountEndpoints.forBooking()}?propertyId=${propertyId}`;
    const response = await get<ApiResponse<BankAccountForBooking[]>>(endpoint);
    return response.data;
  }

  async create(data: CreateBankAccountRequest): Promise<ApiResponse<BankAccount>> {
    const response = await post<ApiResponse<BankAccount>>(bankAccountEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateBankAccountRequest): Promise<ApiResponse<BankAccount>> {
    const response = await put<ApiResponse<BankAccount>>(bankAccountEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(bankAccountEndpoints.delete(id), {
      params: { rowVersion },
    });
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    let endpoint = bankAccountEndpoints.validateCode(code);
    if (excludeId) endpoint = `${endpoint}?excludeId=${excludeId}`;
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const bankAccountService = new BankAccountService();
export default bankAccountService;
